/**
 * HazardNotificationContext - Real-time hazard notifications
 * 
 * Polls for new hazards and displays snackbar alerts to all users.
 * Shows hazard type, location, and allows dismissal.
 * 
 * HAZARD CONFIRMATION: When users pass ANY reported hazard, shows
 * human-friendly prompt asking if it's still there. Community validation!
 * 
 * "Someone out here has my back, even if I've never met them."
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Box, Typography, IconButton, Dialog, Button, Chip, Snackbar, Alert } from '@mui/material';
import {
  Close as CloseIcon,
  NavigateNext as NavigateIcon,
  ThumbUp as YesIcon,
  ThumbDown as NoIcon,
  AccessTime as TimeIcon,
  NearMe as DistanceIcon,
  CarCrash as CarCrashIcon,
  SlowMotionVideo as SlowMotionVideoIcon,
  Construction as ConstructionIcon,
  RemoveRoad as RemoveRoadIcon,
  WarningAmber as WarningAmberIcon,
  Water as WaterIcon,
  VisibilityOff as VisibilityOffIcon,
  LocalPolice as LocalPoliceIcon,
  Engineering as EngineeringIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  Badge as BadgeIcon,
  PersonalInjury as PersonalInjuryIcon,
  MedicalServices as MedicalServicesIcon,
  CarRepair as CarRepairIcon,
  Videocam as VideocamIcon,
  Air as AirIcon,
} from '@mui/icons-material';
import { feedbackService, type SoundType, type HapticType } from '../services/feedback';
import { telegram } from '../services/telegram';
import { storeExternalFix, getLastKnownFix } from '../services/highPrecisionLocation';
import { useAppStore } from '../stores/appStore';
import { roadHazardsService } from '../services/roadHazards';
import { isSantaFeRestroomPin } from '../services/amenityPinService';


// Haversine distance calculation (meters)
function getDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000; // Earth radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

/**
 * Register the current user as a responder en route to an SOS hazard. The
 * injured user's SosCheckinController polls /api/sos/responders to render
 * the list. Failure is non-fatal — routing still works.
 */
async function registerSosResponder(
  hazard: { id: string; latitude: number; longitude: number },
  channel: 'ar' | 'map' | 'navigate' | 'phone',
): Promise<void> {
  try {
    const last = getLastKnownFix();
    const distanceMi = last
      ? (getDistanceMeters(last.lat, last.lng, hazard.latitude, hazard.longitude) / 1609.344)
      : null;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const userId = telegram.getUserId();
    if (userId) headers['X-Telegram-User-Id'] = userId;
    const displayName = telegram.getUser()?.first_name || null;
    await fetch('/api/sos/respond', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        hazardId: hazard.id,
        channel,
        displayName,
        latitude: last?.lat ?? null,
        longitude: last?.lng ?? null,
        distanceMi,
      }),
    });
  } catch (err) {
    console.warn('[SOS responder register]', err);
  }
}

// UNIVERSAL 3-TIER PROXIMITY THRESHOLDS
// Each hazard type has up to 3 tiers with escalating alerts.
// Distances in meters. Tier 1 = early heads-up, Tier 3 = imminent.

interface HazardTierConfig {
  tiers: number[];  // [tier1_meters, tier2_meters, ...] — outermost first, descending
  sound: SoundType;
  haptics: HapticType[]; // per tier (same length as tiers)
  durations: number[]; // toast auto-dismiss ms per tier (0 = persistent)
}

const HAZARD_TIERS: Record<string, HazardTierConfig> = {
  // High urgency — cops, DOT, safety, flock (5mi / 2mi / 0.5mi)
  // Durations extended from 3s/5s to 7s/10s so drivers have time to read + react.
  cop:              { tiers: [8050, 3220, 805], sound: 'copAlert',     haptics: ['light', 'warning', 'heavy'], durations: [7000, 10000, 0] },
  dot_officer:      { tiers: [8050, 3220, 805], sound: 'dotAlert',     haptics: ['light', 'warning', 'heavy'], durations: [7000, 10000, 0] },
  safety_inspector: { tiers: [8050, 3220, 805], sound: 'safetyAlert',  haptics: ['light', 'warning', 'heavy'], durations: [7000, 10000, 0] },
  flock_camera:     { tiers: [8050, 3220, 805], sound: 'flockChirp',   haptics: ['light', 'warning', 'heavy'], durations: [7000, 10000, 0] },
  caliche_trail_camera: { tiers: [8050, 3220, 805], sound: 'flockChirp', haptics: ['light', 'warning', 'heavy'], durations: [7000, 10000, 0] },
  // PTZ cameras sit on most city intersections — only alert when truly on top of one
  // (~100m, roughly a football field). Avoids spamming the modal in dense urban areas.
  ptz_camera:       { tiers: [100], sound: 'flockChirp',   haptics: ['heavy'], durations: [10000] },

  // Critical urgency — crash, fire, injury
  // Extended range: oilfield workers 1-2 hours out need to know about crashes on their route.
  // Injuries in remote desert locations — anyone nearby could assist.
  // 50mi / 20mi / 5mi / 1mi — widest geofence for life-safety events
  crash:            { tiers: [80470, 32190, 8050, 1610], sound: 'crashAlert',   haptics: ['light', 'light', 'warning', 'heavy'], durations: [10000, 10000, 10000, 0] },
  fire:             { tiers: [80470, 32190, 8050, 1610], sound: 'fireAlert',    haptics: ['light', 'light', 'warning', 'heavy'], durations: [10000, 10000, 10000, 0] },
  personal_injury:  { tiers: [80470, 32190, 8050, 1610], sound: 'crashAlert',   haptics: ['light', 'light', 'warning', 'heavy'], durations: [10000, 10000, 15000, 0] },
  injured_worker:   { tiers: [80470, 32190, 8050, 1610], sound: 'crashAlert',   haptics: ['light', 'light', 'warning', 'heavy'], durations: [10000, 10000, 15000, 0] },

  // Low urgency — construction, slowdown, objects, flood (2mi / 0.5mi)
  construction:     { tiers: [3220, 805, 0],    sound: 'constructionAlert', haptics: ['light', 'medium', 'medium'], durations: [7000, 10000, 0] },
  lane_closure:     { tiers: [3220, 805, 0],    sound: 'constructionAlert', haptics: ['light', 'medium', 'medium'], durations: [7000, 10000, 0] },
  slowdown:         { tiers: [3220, 805, 0],    sound: 'hazardAlert',  haptics: ['light', 'medium', 'medium'], durations: [7000, 10000, 0] },
  object_on_road:   { tiers: [3220, 805, 0],    sound: 'hazardAlert',  haptics: ['light', 'medium', 'medium'], durations: [7000, 10000, 0] },
  flooded_road:     { tiers: [3220, 805, 0],    sound: 'floodAlert',   haptics: ['light', 'warning', 'medium'], durations: [7000, 10000, 0] },
  low_visibility:   { tiers: [3220, 805, 0],    sound: 'hazardAlert',  haptics: ['light', 'medium', 'medium'], durations: [7000, 10000, 0] },
  truck_broke_down: { tiers: [16090, 3220, 805], sound: 'hazardAlert',  haptics: ['light', 'medium', 'medium'], durations: [7000, 10000, 0] }, // 10mi/2mi/0.5mi — someone nearby might help
};

const DEFAULT_TIER_CONFIG: HazardTierConfig = {
  tiers: [3220, 805, 0],
  sound: 'hazardAlert',
  haptics: ['light', 'medium', 'medium'],
  durations: [7000, 10000, 0],
};

interface HazardReport {
  id: string;
  type: string;
  latitude: number;
  longitude: number;
  note: string | null;
  context_note?: string | null;
  /** Server-enriched road / intersection / nearest landmark string */
  location_context?: string | null;
  is_ghost: boolean;
  confirmations?: number;
  still_there_votes?: number;
  gone_votes?: number;
  extension_applied?: boolean;
  created_at: string;
  expires_at: string;
}

interface HazardNotification {
  hazard: HazardReport;
  timestamp: number;
}

interface PendingHazardZoom {
  lat: number;
  lng: number;
  zoom: number;
}

interface HazardNotificationContextType {
  hazards: HazardReport[];
  showHazardNotification: (hazard: HazardReport) => void;
  dismissNotification: () => void;
  navigateToHazard: (hazard: HazardReport) => void;
  voteOnHazard: (hazardId: string, vote: 'yes' | 'no') => Promise<void>;
  zoomToHazard: (hazard: HazardReport) => void;
  setZoomCallback: (callback: ((lat: number, lng: number, zoom?: number) => void) | null) => void;
  /** Check for a pending hazard zoom target (used by map on mount) */
  getPendingHazardZoom: () => PendingHazardZoom | null;
  /** Clear the pending hazard zoom after it's been consumed */
  clearPendingHazardZoom: () => void;
}

const HazardNotificationContext = createContext<HazardNotificationContextType | null>(null);

function isArLiveOpen(): boolean {
  return Boolean((window as any).__ziaArLiveOpen);
}

// Hazard type metadata — uses the same MUI icons as RoadHazardDialog
const HAZARD_META: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  crash:           { icon: <CarCrashIcon sx={{ fontSize: 32, color: '#ef4444' }} />,            color: '#ef4444', label: 'Crash' },
  slowdown:        { icon: <SlowMotionVideoIcon sx={{ fontSize: 32, color: '#f59e0b' }} />,     color: '#f59e0b', label: 'Slowdown' },
  construction:    { icon: <ConstructionIcon sx={{ fontSize: 32, color: '#f97316' }} />,        color: '#f97316', label: 'Construction' },
  lane_closure:    { icon: <RemoveRoadIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />,          color: '#8b5cf6', label: 'Lane Closure' },
  object_on_road:  { icon: <WarningAmberIcon sx={{ fontSize: 32, color: '#eab308' }} />,        color: '#eab308', label: 'Object on Road' },
  flooded_road:    { icon: <WaterIcon sx={{ fontSize: 32, color: '#0ea5e9' }} />,               color: '#0ea5e9', label: 'Flooded Road' },
  low_visibility:  { icon: <VisibilityOffIcon sx={{ fontSize: 32, color: '#64748b' }} />,       color: '#64748b', label: 'Low Visibility' },
  dust_storm:      { icon: <AirIcon sx={{ fontSize: 32, color: '#d97706' }} />,                  color: '#d97706', label: 'Dust Storm' },
  cop:             { icon: <LocalPoliceIcon sx={{ fontSize: 32, color: '#3b82f6' }} />,         color: '#3b82f6', label: 'Cop Spotted' },
  safety_inspector:{ icon: <EngineeringIcon sx={{ fontSize: 32, color: '#f97316' }} />,         color: '#f97316', label: 'Safety Inspector' },
  fire:            { icon: <LocalFireDepartmentIcon sx={{ fontSize: 32, color: '#f97316' }} />, color: '#f97316', label: 'Fire' },
  dot_officer:     { icon: <BadgeIcon sx={{ fontSize: 32, color: '#38bdf8' }} />,               color: '#38bdf8', label: 'DOT Officer' },
  personal_injury: { icon: <PersonalInjuryIcon sx={{ fontSize: 32, color: '#ef4444' }} />,      color: '#ef4444', label: 'Personal Injury' },
  injured_worker:  { icon: <MedicalServicesIcon sx={{ fontSize: 32, color: '#f43f5e' }} />,     color: '#f43f5e', label: 'Injured Worker' },
  truck_broke_down:{ icon: <CarRepairIcon sx={{ fontSize: 32, color: '#f97316' }} />,           color: '#f97316', label: 'Truck Broke Down' },
  flock_camera:    { icon: <VideocamIcon sx={{ fontSize: 32, color: '#a855f7' }} />,             color: '#a855f7', label: 'Flock Camera' },
  ptz_camera:      { icon: <VideocamIcon sx={{ fontSize: 32, color: '#22d3ee' }} />,             color: '#22d3ee', label: 'PTZ Camera' },
  caliche_trail_camera: { icon: <VideocamIcon sx={{ fontSize: 32, color: '#facc15' }} />,        color: '#facc15', label: 'Caliche Trail Camera' },
  amenity_porta_potty: { icon: <Typography sx={{ fontSize: 32, lineHeight: 1 }}>💩</Typography>, color: '#84cc16', label: 'Porta Potty' },
  amenity_restroom: { icon: <Typography sx={{ fontSize: 32, lineHeight: 1 }}>💩</Typography>, color: '#84cc16', label: 'Restroom' },
};

interface HazardNotificationProviderProps {
  children: ReactNode;
  pollInterval?: number; // milliseconds
  enabled?: boolean;
}

export const HazardNotificationProvider: React.FC<HazardNotificationProviderProps> = ({
  children,
  pollInterval = 15000, // Poll every 15 seconds for faster hazard propagation
  enabled = true,
}) => {
  const [hazards, setHazards] = useState<HazardReport[]>([]);
  const [flockCameras, setFlockCameras] = useState<HazardReport[]>([]);
  const [notification, setNotification] = useState<HazardNotification | null>(null);
  const [seenHazardIds, _setSeenHazardIds] = useState<Set<string>>(() => {
    // Load seen hazards from localStorage
    const saved = localStorage.getItem('seen_hazard_ids');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  
  // Track hazards user has voted on (persistent in localStorage)
  const [votedHazardIds, setVotedHazardIds] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('voted_hazard_ids');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  
  // Callback for zooming the map to a hazard location
  const zoomCallbackRef = useRef<((lat: number, lng: number, zoom?: number) => void) | null>(null);
  
  // Pending hazard zoom - stored when "View on Map" is clicked but map isn't open yet
  const pendingHazardZoomRef = useRef<PendingHazardZoom | null>(null);
  
  // Access store to open favorites map
  const setShowFavoritesMap = useAppStore((state) => state.setShowFavoritesMap);
  
  // Track which hazards we've already notified about (hazardId -> timestamp)
  // This prevents re-notifying for the same hazard during a single drive-by
  const [notifiedHazardIds, _setNotifiedHazardIds] = useState<Map<string, number>>(new Map());
  // For two-stage alerts (cop / DOT / safety inspector): tracks hazards whose
  // FAR transient alert has already fired. The CLOSE-range persistent alert can
  // still fire later when the user actually drives into the inner radius.
  const farAlertedRef = useRef<Set<string>>(new Set());
  const globalPromptBaselineReadyRef = useRef(false);
  const cameraPromptBaselineReadyRef = useRef(false);
  const amenityPromptBaselineReadyRef = useRef(false);
  const globallyPromptedHazardIdsRef = useRef<Set<string>>(new Set());
  // Track user's current speed (m/s) for smarter notifications
  const userSpeedRef = useRef<number>(0);
  
  const [votingInProgress, setVotingInProgress] = useState(false);
  // SOS all-clear toast — fires when an injured user holds I'M FINE 3s and
  // the controller dispatches `zia:sos-cleared`. Auto-hides after 5s.
  const [sosClearedToast, setSosClearedToast] = useState<{ open: boolean; reporterName: string } | null>(null);

  // Geolocation watch ref
  // watchIdRef removed — using cached GPS polling instead of separate watchPosition
  const userLocationRef = useRef<{ lat: number; lon: number } | null>(null);

  // Save voted hazard IDs to localStorage
  useEffect(() => {
    localStorage.setItem('voted_hazard_ids', JSON.stringify(Array.from(votedHazardIds)));
  }, [votedHazardIds]);

  // SOS all-clear listener — when an injured user holds I'M FINE 3s, the
  // SosCheckinController dispatches `zia:sos-cleared`. We respond two ways:
  //   1. If the responder still has the alert dialog open for that hazard,
  //      auto-dismiss it (the incident is resolved).
  //   2. Always show a 5s green Snackbar so the responder knows the user is OK.
  useEffect(() => {
    const handleSosCleared = (event: Event) => {
      const detail = (event as CustomEvent<{ hazardId?: string; reporterName?: string }>).detail || {};
      const reporterName = detail.reporterName || 'Worker';
      // Auto-dismiss the dialog if it's open on the same hazard.
      setNotification((prev) => {
        if (prev && detail.hazardId && prev.hazard.id === detail.hazardId) {
          return null;
        }
        return prev;
      });
      setSosClearedToast({ open: true, reporterName });
      try { feedbackService.playSound('notification'); } catch { /* non-fatal */ }
    };
    window.addEventListener('zia:sos-cleared', handleSosCleared as EventListener);
    return () => window.removeEventListener('zia:sos-cleared', handleSosCleared as EventListener);
  }, []);

  const refreshCameraAlerts = useCallback(async () => {
    // Fetch camera display layers for map/AR; camera rows are excluded from modal alerts below.
    // Reporter identity intentionally omitted — all reports are anonymous to viewers.
    const mapCameraToHazard = (c: any, type: 'flock_camera' | 'ptz_camera' | 'caliche_trail_camera'): HazardReport => ({
      id: c.id,
      type,
      latitude: c.latitude,
      longitude: c.longitude,
      note: c.note,
      reporter_id: null,
      reporter_name: 'Anonymous',
      votes: c.votes,
      is_ghost: c.is_ghost,
      ghost_at: c.ghost_at,
      created_at: c.created_at,
      expires_at: c.expires_at,
      location_context: c.location_context,
    } as any);

    const accumulated: HazardReport[] = [];
    try {
      const res = await fetch('/api/flock-cameras');
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.cameras) {
          accumulated.push(...data.cameras.map((c: any) => mapCameraToHazard(c, 'flock_camera')));
        }
      }
    } catch (error) {
      console.error('Error fetching flock cameras:', error);
    }
    try {
      const res = await fetch('/api/ptz-cameras');
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.cameras) {
          accumulated.push(...data.cameras.map((c: any) => mapCameraToHazard(c, 'ptz_camera')));
        }
      }
    } catch (error) {
      console.error('Error fetching PTZ cameras:', error);
    }
    try {
      const res = await fetch('/api/caliche-trail-cameras');
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.cameras) {
          accumulated.push(...data.cameras.map((c: any) => mapCameraToHazard(c, 'caliche_trail_camera')));
        }
      }
    } catch (error) {
      console.error('Error fetching Caliche trail cameras:', error);
    }
    if (!cameraPromptBaselineReadyRef.current) {
      cameraPromptBaselineReadyRef.current = true;
      accumulated.forEach((camera) => globallyPromptedHazardIdsRef.current.add(`camera-${camera.type}-${camera.id}`));
    } else {
      accumulated.forEach((camera) => globallyPromptedHazardIdsRef.current.add(`camera-${camera.type}-${camera.id}`));
    }
    setFlockCameras(accumulated);
  }, []);

  const refreshAmenityAlerts = useCallback(async () => {
    try {
      const res = await fetch('/api/poop-pins', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      const pins = Array.isArray(data?.pins) ? data.pins : [];
      const restroomPins: HazardReport[] = pins
        .filter((pin: any) => !isSantaFeRestroomPin(pin))
        .filter((pin: any) => ['porta_potty', 'restroom'].includes(String(pin.pin_type || '').toLowerCase()))
        .map((pin: any) => ({
          id: String(pin.id),
          type: `amenity_${String(pin.pin_type || 'porta_potty').toLowerCase()}`,
          latitude: Number(pin.latitude),
          longitude: Number(pin.longitude),
          note: pin.note ?? null,
          reporter_id: pin.telegram_user_id ?? null,
          reporter_name: pin.telegram_username || 'Anonymous',
          votes: pin.votes ?? pin.score ?? 0,
          is_ghost: Boolean(pin.is_ghost),
          ghost_at: pin.ghost_at ?? null,
          created_at: pin.created_at,
          expires_at: pin.expires_at,
          location_context: null,
        } as any))
        .filter((pin: HazardReport) => Number.isFinite(pin.latitude) && Number.isFinite(pin.longitude));

      if (!amenityPromptBaselineReadyRef.current) {
        amenityPromptBaselineReadyRef.current = true;
        restroomPins.forEach((pin) => globallyPromptedHazardIdsRef.current.add(`amenity-${pin.type}-${pin.id}`));
        return;
      }

      const freshPin = restroomPins.find((pin) => !globallyPromptedHazardIdsRef.current.has(`amenity-${pin.type}-${pin.id}`));
      if (!freshPin) return;
      globallyPromptedHazardIdsRef.current.add(`amenity-${freshPin.type}-${freshPin.id}`);
      if (isArLiveOpen()) return;
      setNotification({ hazard: freshPin, timestamp: Date.now() });
      feedbackService.playSound('notification');
      telegram.hapticFeedback('light');
    } catch (error) {
      console.error('Error fetching amenity reports:', error);
    }
  }, []);

  // Save seen hazards to localStorage
  useEffect(() => {
    localStorage.setItem('seen_hazard_ids', JSON.stringify(Array.from(seenHazardIds)));
  }, [seenHazardIds]);
  
  // Two-tier proximity system:
  // 1. NOTIFICATION - Alert user when approaching (larger radius)
  // 2. CONFIRMATION - Ask if still there when passing by (smaller radius)
  const checkHazardProximity = useCallback((position: GeolocationPosition) => {
    const userLat = position.coords.latitude;
    const userLon = position.coords.longitude;
    userLocationRef.current = { lat: userLat, lon: userLon };

    storeExternalFix({
      lat: userLat,
      lng: userLon,
      accuracy:
        typeof position.coords.accuracy === 'number'
          ? Math.max(position.coords.accuracy, 0)
          : null,
      timestamp: position.timestamp,
      source: 'browser-watch',
    });
    
    // Update user speed (m/s)
    if (position.coords.speed !== null && position.coords.speed >= 0) {
      userSpeedRef.current = position.coords.speed;
    }
    
    const now = Date.now();
    // Cooldown handled by tier tracking — each tier fires once per hazard
    
    // Sort active road hazards by distance (closest first). PTZ/Flock/Caliche cameras are
    // shown on maps/AR only; they should not open the global report modal.
    const allAlerts = [...hazards, ...flockCameras.filter(hazard => hazard.type !== 'flock_camera' && hazard.type !== 'ptz_camera' && hazard.type !== 'caliche_trail_camera')];
    const hazardsWithDistance = allAlerts.map(hazard => ({
      hazard,
      distance: getDistanceMeters(userLat, userLon, hazard.latitude, hazard.longitude)
    })).sort((a, b) => a.distance - b.distance);
    
    // ── Two-stage alerts for cops / DOT / safety inspector ────────────────
    // 1. FAR heads-up at the outermost tier (~5 mi) — auto-dismisses in 8s.
    // 2. CLOSE-range alert at the innermost tier (~0.5 mi) — persistent, the
    //    user manually dismisses or votes. After the close alert fires, the
    //    hazard is added to seenHazardIds so it never re-alerts.
    // Cameras (flock / PTZ / Caliche) get a single auto-dismissing alert.
    // Everything else gets a single persistent alert.
    const TWO_STAGE_TYPES = new Set(['cop', 'dot_officer', 'safety_inspector']);
    const CLOSE_RANGE_M = 805; // ~0.5 mi

    for (const { hazard, distance } of hazardsWithDistance) {
      if (votedHazardIds.has(hazard.id)) continue;
      if (seenHazardIds.has(hazard.id)) continue;

      const config = HAZARD_TIERS[hazard.type] || DEFAULT_TIER_CONFIG;
      const triggerRadius = config.tiers.find(t => t > 0) || DEFAULT_TIER_CONFIG.tiers[0];
      if (distance > triggerRadius) continue;

      const isTwoStage = TWO_STAGE_TYPES.has(hazard.type);
      const isCloseRange = distance <= CLOSE_RANGE_M;
      const alreadyFarAlerted = farAlertedRef.current.has(hazard.id);

      // For two-stage hazards: only fire close-range alert once we're in close
      // range. The far alert can fire only once per hazard per session.
      if (isTwoStage && !isCloseRange && alreadyFarAlerted) continue;

      const isPersistentAlert = isTwoStage ? isCloseRange : (hazard.type !== 'flock_camera' && hazard.type !== 'ptz_camera' && hazard.type !== 'caliche_trail_camera');

      const distMiles = (distance / 1609.34).toFixed(1);
      const typeLabel = hazard.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const label = `⚠️ ${typeLabel.toUpperCase()} AHEAD — ${distMiles} mi`;

      const lastTierIdx = Math.max(0, config.haptics.length - 1);
      telegram.hapticFeedback(config.haptics[lastTierIdx] as any);
      feedbackService.playSound(config.sound);

      setNotification({
        hazard: { ...hazard, note: label },
        timestamp: now,
      });

      if (isPersistentAlert) {
        // Persistent → mark as fully seen so we never re-alert for this hazard
        _setSeenHazardIds(prev => {
          const next = new Set(prev);
          next.add(hazard.id);
          return next;
        });
      } else {
        // Transient → auto-dismiss. Cameras get a quick 3s flash; cop/DOT/safety
        // far alerts get 8s so the user has time to read the road context.
        const isCamera = hazard.type === 'flock_camera' || hazard.type === 'ptz_camera' || hazard.type === 'caliche_trail_camera';
        const dismissAfterMs = isCamera ? 3000 : 8000;
        setTimeout(() => {
          setNotification(prev => prev?.hazard.id === hazard.id ? null : prev);
        }, dismissAfterMs);

        if (isTwoStage) {
          // Track that we've fired the FAR alert; the persistent close-range
          // alert can still fire later when the user actually arrives.
          farAlertedRef.current.add(hazard.id);
        } else {
          // Single-shot transient (cameras) → mark as fully seen
          _setSeenHazardIds(prev => {
            const next = new Set(prev);
            next.add(hazard.id);
            return next;
          });
        }
      }

      break; // One alert at a time
    }
  }, [hazards, flockCameras, notifiedHazardIds, seenHazardIds, votedHazardIds]);
  
  // Poll cached GPS fix for hazard proximity — avoids triggering a separate watchPosition prompt
  useEffect(() => {
    if (!enabled || hazards.length === 0) return;

    const poll = () => {
      try {
        const fix = getLastKnownFix(30000); // 30s max age
        if (fix) {
          checkHazardProximity({ coords: { latitude: fix.lat, longitude: fix.lng } } as GeolocationPosition);
        }
      } catch { /* ignore */ }
    };
    poll();
    const iv = setInterval(poll, 10000); // check every 10s
    return () => clearInterval(iv);
  }, [enabled, hazards, checkHazardProximity]);

  // Subscribe to shared live hazard updates
  useEffect(() => {
    if (!enabled) return;

    setHazards(roadHazardsService.getHazards() as HazardReport[]);
    roadHazardsService.startLiveUpdates(3000);

    const unsubscribe = roadHazardsService.subscribe((nextHazards, newHazards) => {
      setHazards(nextHazards as HazardReport[]);
      if (!globalPromptBaselineReadyRef.current) {
        globalPromptBaselineReadyRef.current = true;
        nextHazards.forEach((hazard) => globallyPromptedHazardIdsRef.current.add(hazard.id));
        return;
      }
      const freshHazard = newHazards.find((hazard) => !globallyPromptedHazardIdsRef.current.has(hazard.id));
      if (!freshHazard) return;
      globallyPromptedHazardIdsRef.current.add(freshHazard.id);
      if (isArLiveOpen()) return;
      setNotification({ hazard: freshHazard as HazardReport, timestamp: Date.now() });
      const meta = HAZARD_META[freshHazard.type];
      feedbackService.playSound(freshHazard.type === 'fire' ? 'fireAlert' : 'notification');
      telegram.hapticFeedback(meta ? 'medium' : 'light');
    });

    return () => {
      unsubscribe();
      roadHazardsService.stopLiveUpdates();
    };
  }, [enabled]);

  useEffect(() => {
    const onArState = () => {
      if (isArLiveOpen()) {
        setNotification(null);
      }
    };
    window.addEventListener('zia:ar-live-open-state', onArState);
    return () => window.removeEventListener('zia:ar-live-open-state', onArState);
  }, []);

  // Poll for camera overlays used in proximity notifications
  useEffect(() => {
    if (!enabled) return;

    void refreshCameraAlerts();
    const interval = setInterval(refreshCameraAlerts, pollInterval);

    return () => clearInterval(interval);
  }, [enabled, pollInterval, refreshCameraAlerts]);

  useEffect(() => {
    if (!enabled) return;

    void refreshAmenityAlerts();
    const interval = setInterval(refreshAmenityAlerts, pollInterval);

    return () => clearInterval(interval);
  }, [enabled, pollInterval, refreshAmenityAlerts]);

  // Show notification for a hazard
  const showHazardNotification = useCallback((hazard: HazardReport) => {
    if (hazard.type === 'flock_camera' || hazard.type === 'ptz_camera' || hazard.type === 'caliche_trail_camera') return;
    // Never show notification for hazards user already voted on
    if (votedHazardIds.has(hazard.id)) return;
    
    setNotification({ hazard, timestamp: Date.now() });
    feedbackService.playSound('notification');
    
    // Auto-dismiss after 15 seconds (but don't mark as voted — user can still see it next drive-by).
    // Extended from 8s → 15s so drivers have time to read, understand, and react.
    setTimeout(() => {
      setNotification(prev => {
        if (prev?.hazard.id === hazard.id) {
          return null;
        }
        return prev;
      });
    }, 15000);
  }, [votedHazardIds]);

  const viewHazardInAr = useCallback((hazard: HazardReport) => {
    const targetId = hazard.type === 'flock_camera'
      ? `flk-${hazard.id}`
      : hazard.type === 'ptz_camera'
        ? `ptz-${hazard.id}`
        : hazard.type === 'caliche_trail_camera'
          ? `ctc-${hazard.id}`
        : hazard.type.startsWith('amenity_')
          ? `amn-${hazard.id}`
        : hazard.id;
    setNotification(null);
    window.dispatchEvent(new CustomEvent('zia:view-hazard-in-ar', {
      detail: {
        id: targetId,
        type: hazard.type,
        lat: hazard.latitude,
        lng: hazard.longitude,
      },
    }));
    // For SOS / injury hazards, auto-start AR turn-by-turn navigation so the
    // responder gets the laser route line drawn immediately AND register
    // them in /api/sos/respond so the injured user's SOS modal shows
    // someone is on the way.
    if (hazard.type === 'personal_injury' || hazard.type === 'injured_worker') {
      window.dispatchEvent(new CustomEvent('ar:startNavigation', {
        detail: {
          lat: hazard.latitude,
          lng: hazard.longitude,
          name: 'Injured worker',
        },
      }));
      void registerSosResponder(hazard, 'ar');
    }
  }, []);

  // Dismiss current notification — also mark as voted so it never reappears
  const dismissNotification = useCallback(() => {
    setNotification(prev => {
      if (prev) {
        setVotedHazardIds(prevVoted => {
          const updated = new Set(prevVoted);
          updated.add(prev.hazard.id);
          if (updated.size > 200) {
            const arr = Array.from(updated);
            arr.splice(0, arr.length - 200);
            return new Set(arr);
          }
          return updated;
        });
      }
      return null;
    });
  }, []);

  // Set the zoom callback from the map component
  const setZoomCallback = useCallback((callback: ((lat: number, lng: number, zoom?: number) => void) | null) => {
    zoomCallbackRef.current = callback;
  }, []);

  // Zoom to hazard location on the in-app map
  const zoomToHazard = useCallback((hazard: HazardReport) => {
    // Always ensure the map is visible first
    setShowFavoritesMap(true);

    if (zoomCallbackRef.current) {
      // Map component is initialized - zoom directly
      // Small delay to let the Dialog open/render before zooming
      setTimeout(() => {
        zoomCallbackRef.current?.(hazard.latitude, hazard.longitude, 17);
      }, 300);
    } else {
      // Map isn't initialized yet - store pending zoom for when it loads
      pendingHazardZoomRef.current = { lat: hazard.latitude, lng: hazard.longitude, zoom: 17 };
    }
    feedbackService.playSound('success');
    if (hazard.type === 'personal_injury' || hazard.type === 'injured_worker') {
      void registerSosResponder(hazard, 'map');
    }
  }, [setShowFavoritesMap]);

  // Get pending hazard zoom (called by map on mount)
  const getPendingHazardZoom = useCallback(() => {
    return pendingHazardZoomRef.current;
  }, []);

  // Clear pending hazard zoom after it's consumed
  const clearPendingHazardZoom = useCallback(() => {
    pendingHazardZoomRef.current = null;
  }, []);

  // Navigate to hazard location - direct to Google Maps
  const navigateToHazard = useCallback((hazard: HazardReport) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${hazard.latitude},${hazard.longitude}&travelmode=driving`, '_blank');
    if (hazard.type === 'personal_injury' || hazard.type === 'injured_worker') {
      void registerSosResponder(hazard, 'navigate');
    }
  }, []);

  // Vote on a hazard (yes = still there, no = gone) - uses new voting API
  const voteOnHazard = useCallback(async (hazardId: string, vote: 'yes' | 'no') => {
    if (!userLocationRef.current) {
      feedbackService.playSound('error');
      return;
    }
    
    setVotingInProgress(true);
    try {
      // Convert yes/no to still_there/gone for API
      const voteType = vote === 'yes' ? 'still_there' : 'gone';
      
      const response = await fetch('/api/hazards/vote', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Telegram-User-Id': telegram.getUserId() || '',
        },
        body: JSON.stringify({ 
          hazardId, 
          voteType,
          userId: telegram.getUserId() || 'anonymous',
          userLatitude: userLocationRef.current.lat,
          userLongitude: userLocationRef.current.lon,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        feedbackService.playSound(vote === 'yes' ? 'warning' : 'success');
        
        // Mark this hazard as voted on (persist to localStorage)
        setVotedHazardIds(prev => {
          const updated = new Set(prev);
          updated.add(hazardId);
          // Keep only last 200 voted hazards
          if (updated.size > 200) {
            const arr = Array.from(updated);
            arr.splice(0, arr.length - 200);
            return new Set(arr);
          }
          return updated;
        });
        
        // Always dismiss the notification after voting
        dismissNotification();
        
        // If hazard was removed, refresh hazards list
        if (data.hazardRemoved) {
          void roadHazardsService.fetchHazards();
        }
      } else {
        // Show error feedback (too far, already voted, etc)
        feedbackService.playSound('error');
      }
    } catch (error) {
      console.error('Error voting on hazard:', error);
      feedbackService.playSound('error');
    } finally {
      setVotingInProgress(false);
    }
  }, [dismissNotification]);

  const meta = notification ? HAZARD_META[notification.hazard.type] : null;
  const isGhost = notification?.hazard.is_ghost;

  return (
    <HazardNotificationContext.Provider
      value={{
        hazards,
        showHazardNotification,
        dismissNotification,
        navigateToHazard,
        voteOnHazard,
        zoomToHazard,
        setZoomCallback,
        getPendingHazardZoom,
        clearPendingHazardZoom,
      }}
    >
      {children}
      
      {/* Hazard Notification Modal - Cyberpunk glassmorphism dialog */}
      <Dialog
        open={!!notification && !!meta && !isArLiveOpen()}
        onClose={dismissNotification}
        className="zwn-hazard-alert"
        sx={{ zIndex: 99999 }}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
            border: `2px solid ${isGhost ? 'rgba(148, 163, 184, 0.5)' : (meta?.color || '#f59e0b')}`,
            borderRadius: 3,
            p: 0,
            minWidth: 340,
            maxWidth: 420,
            boxShadow: `0 0 60px ${isGhost ? 'rgba(100, 116, 139, 0.4)' : (meta?.color || '#f59e0b')}40, 0 0 120px ${isGhost ? 'rgba(100, 116, 139, 0.2)' : (meta?.color || '#f59e0b')}20`,
            backdropFilter: 'blur(20px)',
            overflow: 'visible',
          }
        }}
      >
        {notification && meta && (() => {
          const note = notification.hazard.note || '';
          const isDotAlert = notification.hazard.type === 'cop' && /\bDOT\b/i.test(note);
          const label = isDotAlert ? 'DOT Spotted' : meta.label;
          const hazardColor = meta.color;

          return (
            <Box sx={{ position: 'relative' }}>
              {/* Glowing top accent bar */}
              <Box sx={{
                height: 4,
                background: `linear-gradient(90deg, transparent, ${hazardColor}, transparent)`,
                boxShadow: `0 0 20px ${hazardColor}80`,
              }} />

              {/* Close button */}
              <IconButton
                size="small"
                onClick={dismissNotification}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  color: 'rgba(255,255,255,0.5)',
                  zIndex: 1,
                  '&:hover': { color: 'white', background: 'rgba(255,255,255,0.1)' },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <Box sx={{ p: 3, textAlign: 'center' }}>
                {/* Hazard Icon with glow */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${hazardColor}30, ${hazardColor}10)`,
                    border: `3px solid ${hazardColor}80`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    boxShadow: `0 0 30px ${hazardColor}40, inset 0 0 20px ${hazardColor}20`,
                    animation: 'hazardPulse 2s ease-in-out infinite',
                    '@keyframes hazardPulse': {
                      '0%, 100%': { boxShadow: `0 0 30px ${hazardColor}40, inset 0 0 20px ${hazardColor}20` },
                      '50%': { boxShadow: `0 0 50px ${hazardColor}60, inset 0 0 30px ${hazardColor}30` },
                    },
                    opacity: isGhost ? 0.7 : 1,
                  }}
                >
                  {meta.icon}
                </Box>

                {/* Title */}
                <Typography
                  variant="h5"
                  sx={{
                    color: 'white',
                    fontWeight: 800,
                    fontFamily: '"Orbitron", monospace',
                    mb: 0.5,
                    textShadow: `0 0 20px ${hazardColor}60`,
                    letterSpacing: '0.05em',
                  }}
                >
                  {isGhost ? '👻 ' : '⚠️ '}{label} Ahead!
                </Typography>

                {/* Location context — road / intersection / nearest landmark */}
                {notification.hazard.location_context && (
                  <Typography
                    sx={{
                      color: hazardColor,
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      mb: 0.75,
                      textShadow: `0 0 8px ${hazardColor}66`,
                      letterSpacing: '0.02em',
                    }}
                  >
                    📍 {notification.hazard.location_context}
                  </Typography>
                )}

                {/* Note / context */}
                {notification.hazard.note && (
                  <Typography
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.9rem',
                      mb: 1,
                      fontStyle: 'italic',
                    }}
                  >
                    "{notification.hazard.note}"
                  </Typography>
                )}

                {/* Distance and Time chips */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 2, flexWrap: 'wrap' }}>
                  {userLocationRef.current && (
                    <Chip
                      size="small"
                      icon={<DistanceIcon sx={{ fontSize: 16, color: `${hazardColor} !important` }} />}
                      label={(() => {
                        const dist = getDistanceMeters(
                          userLocationRef.current!.lat,
                          userLocationRef.current!.lon,
                          notification.hazard.latitude,
                          notification.hazard.longitude
                        );
                        if (dist < 1000) return `${Math.round(dist)}m away`;
                        return `${(dist / 1609.34).toFixed(1)} mi away`;
                      })()}
                      sx={{
                        background: `${hazardColor}20`,
                        border: `1px solid ${hazardColor}50`,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                      }}
                    />
                  )}
                  <Chip
                    size="small"
                    icon={<TimeIcon sx={{ fontSize: 16, color: `${hazardColor} !important` }} />}
                    label={(() => {
                      const mins = Math.floor((Date.now() - new Date(notification.hazard.created_at).getTime()) / 60000);
                      if (mins < 1) return 'Just now';
                      if (mins < 60) return `${mins} min ago`;
                      return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
                    })()}
                    sx={{
                      background: `${hazardColor}20`,
                      border: `1px solid ${hazardColor}50`,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                    }}
                  />
                </Box>

                {/* Vote counts if available */}
                {(notification.hazard.still_there_votes || notification.hazard.gone_votes) ? (
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 2, flexWrap: 'wrap' }}>
                    {(notification.hazard.still_there_votes || 0) > 0 && (
                      <Chip
                        size="small"
                        icon={<YesIcon sx={{ fontSize: 14, color: '#22c55e !important' }} />}
                        label={`${notification.hazard.still_there_votes} confirmed`}
                        sx={{
                          background: 'rgba(34, 197, 94, 0.15)',
                          border: '1px solid rgba(34, 197, 94, 0.4)',
                          color: '#86efac',
                          fontSize: '0.75rem',
                        }}
                      />
                    )}
                    {(notification.hazard.gone_votes || 0) > 0 && (
                      <Chip
                        size="small"
                        icon={<NoIcon sx={{ fontSize: 14, color: '#ef4444 !important' }} />}
                        label={`${notification.hazard.gone_votes} say gone`}
                        sx={{
                          background: 'rgba(239, 68, 68, 0.15)',
                          border: '1px solid rgba(239, 68, 68, 0.4)',
                          color: '#fca5a5',
                          fontSize: '0.75rem',
                        }}
                      />
                    )}
                  </Box>
                ) : null}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1.1, mb: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<NavigateIcon />}
                    onClick={() => viewHazardInAr(notification.hazard)}
                    sx={{
                      flex: '1 1 140px',
                      py: 1.5,
                      fontWeight: 800,
                      fontSize: '0.85rem',
                      background: `linear-gradient(135deg, ${hazardColor}, ${hazardColor}aa)`,
                      border: `1px solid ${hazardColor}`,
                      color: 'white',
                      boxShadow: `0 0 24px ${hazardColor}40`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${hazardColor}, ${hazardColor}dd)`,
                        boxShadow: `0 0 34px ${hazardColor}55`,
                      },
                    }}
                  >
                    View in AR
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<NavigateIcon />}
                    onClick={() => {
                      // Capture hazard data before dismissing notification
                      const hazard = notification.hazard;
                      // Dismiss notification first so its Dialog fully closes
                      dismissNotification();
                      // Open map after notification Dialog exit animation completes
                      setTimeout(() => {
                        zoomToHazard(hazard);
                      }, 350);
                    }}
                    sx={{
                      flex: '1 1 140px',
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      background: `linear-gradient(135deg, ${hazardColor}cc, ${hazardColor}99)`,
                      border: `1px solid ${hazardColor}`,
                      color: 'white',
                      boxShadow: `0 0 20px ${hazardColor}30`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${hazardColor}, ${hazardColor}cc)`,
                        boxShadow: `0 0 30px ${hazardColor}50`,
                      },
                    }}
                  >
                    View on Map
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<NavigateIcon />}
                    onClick={() => {
                      navigateToHazard(notification.hazard);
                      dismissNotification();
                    }}
                    sx={{
                      flex: '1 1 140px',
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.3), rgba(6, 182, 212, 0.15))',
                      border: '1px solid rgba(6, 182, 212, 0.5)',
                      color: 'white',
                      boxShadow: '0 0 20px rgba(6, 182, 212, 0.2)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.5), rgba(6, 182, 212, 0.3))',
                        boxShadow: '0 0 30px rgba(6, 182, 212, 0.4)',
                      },
                    }}
                  >
                    Navigate
                  </Button>
                </Box>

                {/* Vote Buttons — hidden for stationary infrastructure (flock + PTZ
                    cameras don't move, so "still there / gone" voting doesn't apply)
                    AND hidden for personal_injury / injured_worker SOS hazards
                    (only the injured user clears them via "I'M FINE — hold 3s"). */}
                {notification.hazard.type !== 'flock_camera'
                  && notification.hazard.type !== 'ptz_camera'
                  && notification.hazard.type !== 'caliche_trail_camera'
                  && notification.hazard.type !== 'personal_injury'
                  && notification.hazard.type !== 'injured_worker'
                  && !notification.hazard.type.startsWith('amenity_') && (
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<YesIcon />}
                      disabled={votingInProgress}
                      onClick={() => voteOnHazard(notification.hazard.id, 'yes')}
                      sx={{
                        flex: 1,
                        py: 1.5,
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(239, 68, 68, 0.15))',
                        border: '1px solid rgba(239, 68, 68, 0.5)',
                        color: 'white',
                        '&:hover': {
                          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.5), rgba(239, 68, 68, 0.3))',
                        },
                      }}
                    >
                      Still There
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<NoIcon />}
                      disabled={votingInProgress}
                      onClick={() => voteOnHazard(notification.hazard.id, 'no')}
                      sx={{
                        flex: 1,
                        py: 1.5,
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.3), rgba(34, 197, 94, 0.15))',
                        border: '1px solid rgba(34, 197, 94, 0.5)',
                        color: 'white',
                        '&:hover': {
                          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.5), rgba(34, 197, 94, 0.3))',
                        },
                      }}
                    >
                      Gone
                    </Button>
                  </Box>
                )}

                {/* Dismiss link */}
                <Button
                  onClick={dismissNotification}
                  sx={{
                    mt: 2,
                    color: 'rgba(255, 255, 255, 0.4)',
                    fontSize: '0.8rem',
                    '&:hover': { color: 'rgba(255, 255, 255, 0.7)' },
                  }}
                >
                  Dismiss
                </Button>
              </Box>

              {/* Glowing bottom accent bar */}
              <Box sx={{
                height: 3,
                background: `linear-gradient(90deg, transparent, ${hazardColor}60, transparent)`,
                boxShadow: `0 0 10px ${hazardColor}40`,
              }} />
            </Box>
          );
        })()}
      </Dialog>

      {/* SOS all-clear toast — fired by the injured user's I'M FINE hold. */}
      <Snackbar
        open={Boolean(sosClearedToast?.open)}
        autoHideDuration={5000}
        onClose={() => setSosClearedToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '80px !important' }}
      >
        <Alert
          severity="success"
          variant="filled"
          icon={false}
          sx={{
            fontWeight: 800,
            fontFamily: '"Orbitron", monospace',
            letterSpacing: 0.4,
            backgroundColor: 'rgba(22,163,74,0.94)',
            border: '1.5px solid rgba(134,239,172,0.7)',
            boxShadow: '0 0 22px rgba(34,197,94,0.55)',
          }}
        >
          ✅ {sosClearedToast?.reporterName || 'Worker'} is OK — incident cleared
        </Alert>
      </Snackbar>
    </HazardNotificationContext.Provider>
  );
};

// Hook to use hazard notifications
export const useHazardNotifications = () => {
  const context = useContext(HazardNotificationContext);
  if (!context) {
    throw new Error('useHazardNotifications must be used within HazardNotificationProvider');
  }
  return context;
};

export default HazardNotificationProvider;
